import React, { FC, useCallback } from 'react';

import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { ForgeUIExtensionAnalyticsContext } from '@atlassian/forge-ui';
import { CoreData } from '@atlassian/forge-ui-types';

import { useStatsigGate } from 'src/contexts/feature-provider';

import { ForgeUIType, RendererProps } from '../../types';
import { getForgeUIType, getLocalId } from '../../utils/appData';
import { ForgeIframeRenderer } from '../ForgeIframeRenderer/ForgeIframeRenderer';
import { ForgeUIKit2Renderer } from '../ForgeUIKitRenderer/ForgeUIKit2Renderer';
import { ForgeUIKitRenderer } from '../ForgeUIKitRenderer/ForgeUIKitRenderer';
import { LegacyForgeUIKit2Renderer } from '../ForgeUIKitRenderer/LegacyForgeUIKit2Renderer';

export const ForgeSharedRenderer: FC<RendererProps> = ({
  app,
  shouldCallGetContextToken,
  contextIds,
  workspaceUuid,
  getContextTokenFnGenerator,
  ...props
}) => {
  const localId = getLocalId(app.id, props.extensionData.type);
  const coreData: CoreData = {
    localId,
    workspaceId: workspaceUuid,
  };

  // eslint cannot detect the useCallback dependencies unless I converted this to inline function and call that
  const getContextToken = useCallback(
    () => getContextTokenFnGenerator(app.id)(),
    [app.id, getContextTokenFnGenerator]
  );
  // eslint rule doesn't allow for useCallback to be called conditionally
  const getContextTokenProp = shouldCallGetContextToken
    ? getContextToken
    : undefined;

  const useConsolidatedRenderer = useStatsigGate(
    StatsigFeatureKeys.useConsolidatedForgeRenderer
  ) as boolean;

  const extensionRenderer = (() => {
    if (getForgeUIType(app) === ForgeUIType.CUSTOM_UI) {
      return (
        <ForgeIframeRenderer
          app={app}
          currentUser={props.currentUser}
          coreData={coreData}
          extensionData={props.extensionData}
          onInitialRender={props.onInitialRender}
          bridge={props.bridge}
          contextIds={contextIds}
          getContextToken={getContextTokenProp}
        />
      );
    }

    if (getForgeUIType(app) === ForgeUIType.UI_KIT_2) {
      if (useConsolidatedRenderer) {
        return (
          <ForgeUIKit2Renderer
            app={app}
            getContextToken={getContextTokenProp}
            contextIds={contextIds}
            coreData={coreData}
            {...props}
          />
        );
      } else {
        return (
          <LegacyForgeUIKit2Renderer
            app={app}
            getContextToken={getContextTokenProp}
            contextIds={contextIds}
            coreData={coreData}
            {...props}
          />
        );
      }
    }

    return (
      <ForgeUIKitRenderer
        app={app}
        getContextToken={getContextTokenProp}
        contextIds={contextIds}
        coreData={coreData}
        {...props}
      />
    );
  })();

  return (
    <ForgeUIExtensionAnalyticsContext localId={localId} extensionId={app.id}>
      {extensionRenderer}
    </ForgeUIExtensionAnalyticsContext>
  );
};
